import * as React from 'react'

import { SpringValue, useSpring } from '@react-spring/web'

import {
  BAR_WIDTH_ADJUSTMENT,
  HOVER_SCALE_FACTOR,
} from 'modules/core/components/BarChart/BarItem/constants'
import type {
  BarAnimationConfig,
  SelectionState,
  StackContextValue,
} from 'modules/core/components/BarChart/BarItem/types'

const StackContext = React.createContext<StackContextValue | null>(null)

interface StackProviderProps {
  children: React.ReactNode
  groupMode?: 'grouped' | 'stacked'
}

export const StackProvider: React.FC<StackProviderProps> = ({
  children,
  groupMode = 'grouped',
}) => {
  const [hoveredStackIndex, setHoveredStackIndex] = React.useState<string | null>(null)
  const [selectionState, setSelectionState] = React.useState<SelectionState>({
    selections: [],
  })

  const getCurrentSelections = React.useCallback((): string[] => {
    return selectionState.selections
  }, [selectionState.selections])

  const toggleSelection = React.useCallback((id: string): void => {
    setSelectionState((prev) => {
      const currentSelections = prev.selections
      const newSelections = currentSelections.includes(id)
        ? currentSelections.filter((selectedId) => selectedId !== id)
        : [...currentSelections, id]

      return { selections: newSelections }
    })
  }, [])

  const contextValue = React.useMemo(
    (): StackContextValue => ({
      hoveredStackIndex,
      setHoveredStackIndex,
      selectionState,
      setSelectionState,
      getCurrentSelections,
      toggleSelection,
      groupMode,
    }),
    [hoveredStackIndex, selectionState, getCurrentSelections, toggleSelection, groupMode]
  )

  return <StackContext.Provider value={contextValue}>{children}</StackContext.Provider>
}

export const useStackHover = () => {
  const context = React.useContext(StackContext)
  if (!context) {
    throw new Error('useStackHover must be used within a StackProvider')
  }
  const { hoveredStackIndex, setHoveredStackIndex } = context
  return { hoveredStackIndex, setHoveredStackIndex }
}

export const useStackSelection = () => {
  const context = React.useContext(StackContext)
  if (!context) {
    throw new Error('useStackSelection must be used within a StackProvider')
  }
  const { getCurrentSelections, toggleSelection, selectionState, groupMode } = context
  return {
    selectedStackIndexes: getCurrentSelections(),
    toggleSelection,
    selectionState,
    groupMode,
  }
}

export const useBarAnimations = (
  width: number,
  height: number,
  xAxis: number,
  yAxis: number,
  springHeight: SpringValue<number>,
  isHovered: boolean
): BarAnimationConfig => {
  return useSpring({
    width: isHovered ? width * HOVER_SCALE_FACTOR : width,
    height: springHeight,
    transform: `translate(${isHovered ? xAxis - (width * BAR_WIDTH_ADJUSTMENT) / 2 : xAxis}, ${
      yAxis + height
    }) scale(1, -1)`,
  })
}
